import { AuthenticatedHttpService } from 'http/httpService'
import { ReportingCategory, NewReportingCategory } from './schema'
import { t } from 'translations'

const parseErrorResponse = async (response: Response, fallback: string): Promise<string> => {
    try {
        const body = await response.json()
        const messages = Object.values(body).flat()
        if (messages.length > 0) {
            return messages.join('. ')
        }
    } catch {
        // response body is not JSON
    }
    return fallback
}

export class ReportingCategoriesService {
    constructor(private httpService: AuthenticatedHttpService, private backofficeEndpoint: string) { }

    private baseUrl(accountSlug: string) {
        return `${this.backofficeEndpoint}dashboard_api/accounts/${accountSlug}/reporting-categories/`
    }

    async getReportingCategories(accountSlug: string): Promise<ReportingCategory[]> {
        const response = await this.httpService.fetch(this.baseUrl(accountSlug))
        if (!response.ok) {
            throw new Error(await parseErrorResponse(response, t('settings.accountSettings.reportingCategories.errors.unableToRetrieve')))
        }
        const json = await response.json()
        return json.entries
    }

    async createReportingCategory(accountSlug: string, category: NewReportingCategory): Promise<ReportingCategory> {
        const response = await this.httpService.fetch(this.baseUrl(accountSlug), {
            method: 'POST',
            body: JSON.stringify(category),
            headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
            throw new Error(await parseErrorResponse(response, t('settings.accountSettings.reportingCategories.errors.unableToCreate')))
        }
        return await response.json()
    }

    async updateReportingCategory(accountSlug: string, category: ReportingCategory): Promise<ReportingCategory> {
        const response = await this.httpService.fetch(`${this.baseUrl(accountSlug)}${category.uuid}/`, {
            method: 'PATCH',
            body: JSON.stringify(category),
            headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
            throw new Error(await parseErrorResponse(response, t('settings.accountSettings.reportingCategories.errors.unableToUpdate')))
        }
        return await response.json()
    }

    async assignArticles(accountSlug: string, categoryUuid: string, articleUuids: string[]): Promise<void> {
        const response = await this.httpService.fetch(
            `${this.baseUrl(accountSlug)}${categoryUuid}/assign-articles/`,
            {
                method: 'PATCH',
                body: JSON.stringify({ article_uuids: articleUuids }),
                headers: { 'Content-Type': 'application/json' },
            },
        )
        if (!response.ok) {
            throw new Error(await parseErrorResponse(response, t('settings.accountSettings.reportingCategories.errors.unableToAssignArticles')))
        }
    }

    async deleteReportingCategory(accountSlug: string, uuid: string): Promise<void> {
        const response = await this.httpService.fetch(`${this.baseUrl(accountSlug)}${uuid}/`, {
            method: 'DELETE',
        })
        if (!response.ok) {
            throw new Error(await parseErrorResponse(response, t('settings.accountSettings.reportingCategories.errors.unableToDelete')))
        }
    }
}
